import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Route as RouteIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import {
  Authorize,
  Table,
  GlobalButton,
  RowActionsMenu,
  type RowAction,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  BUSINESS_ROLE_GROUPS,
  SHIPPING_PERMISSIONS,
  userHasPermission,
} from '@/components/security/AppRoles';
import { useSortableData, useUserInfo } from '@/hooks';
import { ColumnConfig } from '@/components/table/Table';
import { shippingsTableColumns } from '../config/columns';
import { ShippingListApi } from '../models';
import {
  useShippingSelection,
  useShippingRouting,
  usePrintLabel,
} from '../hooks';
import AssignShippingModal from './AssignShippingModal';
import BatchLabelPrint from './BatchLabelPrint';

interface ShippingsTableProps {
  shippings: ShippingListApi[];
  isLoading?: boolean;
  totalItems?: number;
  totalPages?: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  logisticOperatorId?: string;
  basePath?: string;
  columns?: ColumnConfig[];
  editableStatusIds?: string[];
}

const ShippingsTable = ({
  shippings,
  isLoading = false,
  totalItems = 0,
  totalPages = 0,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  logisticOperatorId = '',
  basePath = '/business',
  columns: columnsProp,
  editableStatusIds,
}: ShippingsTableProps) => {
  const navigate = useNavigate();
  const [assigningShipping, setAssigningShipping] =
    useState<ShippingListApi | null>(null);
  const { permissions } = useUserInfo();
  const canAccessRouting = userHasPermission(
    permissions,
    BUSINESS_ROLE_GROUPS.adminOrDispatcher
  );
  const canAccessLabelPrinting = userHasPermission(
    permissions,
    SHIPPING_PERMISSIONS.labels
  );
  const canShowSelection = canAccessRouting || canAccessLabelPrinting;

  const canBeRouted = useCallback((item: ShippingListApi) => {
    const name = item.status?.name;
    return name === 'PENDING' || name === 'NOT_DELIVERED';
  }, []);

  const canAssignShipping = useCallback((shipping: ShippingListApi) => {
    const isFinalStatus = [
      'DELIVERED',
      'CANCELLED',
      'CANCELADO',
      'ELIMINADO',
      'PICKUP_DONE',
    ].includes(shipping.status?.name?.toUpperCase() ?? '');
    const isOnRoute = shipping.status?.name === 'ON_ROUTE';
    return !isOnRoute && !isFinalStatus;
  }, []);

  const columns = useMemo(
    () => columnsProp ?? shippingsTableColumns,
    [columnsProp]
  );

  const { sortedData, selectedSort, handleSort } = useSortableData({
    data: shippings,
    currentPage: currentPage,
  });

  const {
    selectedShippings,
    handleSelectAll,
    handleSelectShipping,
    MAX_SHIPPINGS_PER_ROUTING,
  } = useShippingSelection({
    sortedData,
    canSelectRow: canAccessRouting ? canBeRouted : undefined,
  });

  const { handleRouteSelected, handleRouteAll } = useShippingRouting({
    sortedData,
    selectedShippings,
    logisticOperatorId,
    maxSelectable: MAX_SHIPPINGS_PER_ROUTING,
  });

  const { handlePrintLabel, isPrinting, handleDownloadLabel, isDownloading } =
    usePrintLabel();

  const canClone = userHasPermission(
    permissions,
    BUSINESS_ROLE_GROUPS.operations
  );

  const buildSecondaryActions = (shipping: ShippingListApi): RowAction[] => [
    {
      key: 'clone',
      label: 'Clonar pedido',
      icon: <ContentCopyIcon fontSize='small' />,
      onClick: () => navigate(`${basePath}/shipping/${shipping.number}/clone`),
      hidden: !canClone,
    },
    {
      key: 'print',
      label: 'Imprimir etiqueta',
      icon: <PrintIcon fontSize='small' />,
      onClick: () => handlePrintLabel(shipping),
      disabled: isPrinting,
    },
    {
      key: 'download',
      label: 'Descargar etiqueta',
      icon: <DownloadIcon fontSize='small' />,
      onClick: () => handleDownloadLabel(shipping),
      disabled: isDownloading,
    },
    {
      key: 'assign',
      label: 'Asignar conductor',
      icon: <PersonAddIcon fontSize='small' />,
      onClick: () => setAssigningShipping(shipping),
      hidden: !canAccessRouting || !canAssignShipping(shipping),
    },
  ];

  const renderActions = (rowData: Record<string, any>) => {
    const shipping = rowData as ShippingListApi;
    const isEditable =
      !editableStatusIds || editableStatusIds.includes(shipping.statusId ?? '');

    return (
      <div className='flex flex-col 2xl:flex-row justify-end gap-1 2xl:gap-2'>
        <IconButton
          onClick={() => navigate(`${basePath}/shipping/${shipping.number}`)}
          title='Ver detalles'
          size='small'
        >
          <VisibilityIcon />
        </IconButton>
        {isEditable && (
          <Authorize permission={SHIPPING_PERMISSIONS.edit} fallback={null}>
            <IconButton
              onClick={() =>
                navigate(`${basePath}/shipping/${shipping.number}/edit`, {
                  state: { from: 'list' },
                })
              }
              title='Editar envío'
              size='small'
            >
              <EditIcon />
            </IconButton>
          </Authorize>
        )}
        <RowActionsMenu actions={buildSecondaryActions(shipping)} />
      </div>
    );
  };

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      <div className='flex items-center justify-between gap-4 pb-4 border-b'>
        <div className='flex items-center gap-4'>
          <Authorize
            permission={BUSINESS_ROLE_GROUPS.adminOrDispatcher}
            fallback={null}
          >
            <span className='text-sm text-gray-600'>
              {selectedShippings.size > 0
                ? `${selectedShippings.size} envío${selectedShippings.size > 1 ? 's' : ''} seleccionado${selectedShippings.size > 1 ? 's' : ''}`
                : 'Ningún envío seleccionado'}
            </span>
            {selectedShippings.size >= MAX_SHIPPINGS_PER_ROUTING && (
              <span className='text-sm text-orange-600 font-semibold'>
                Límite de {MAX_SHIPPINGS_PER_ROUTING} envíos alcanzado
              </span>
            )}
          </Authorize>
        </div>
        <div className='flex items-center gap-2'>
          <Authorize
            permission={BUSINESS_ROLE_GROUPS.adminOrDispatcher}
            fallback={null}
          >
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={handleRouteAll}
              disabled={
                sortedData.length === 0 ||
                sortedData.length > MAX_SHIPPINGS_PER_ROUTING
              }
            >
              <RouteIcon />
              Rutear todos
            </GlobalButton>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={handleRouteSelected}
              disabled={selectedShippings.size === 0}
            >
              <RouteIcon />
              Rutear ({selectedShippings.size})
            </GlobalButton>
          </Authorize>
          <Authorize permission={SHIPPING_PERMISSIONS.labels} fallback={null}>
            <BatchLabelPrint selectedShippings={selectedShippings} />
          </Authorize>
        </div>
      </div>

      {isLoading || sortedData?.length > 0 ? (
        <div className='overflow-x-auto'>
          <Table
            data={sortedData}
            renderActions={renderActions}
            columns={columns}
            selectedSort={selectedSort}
            handleSort={handleSort}
            isLoading={isLoading}
            selectable={canShowSelection}
            {...(canShowSelection && {
              isRowSelectable: canAccessRouting
                ? (rowData) => {
                    const item = rowData as unknown;
                    return canBeRouted(item as ShippingListApi);
                  }
                : undefined,
              selectedKeys: selectedShippings,
              maxSelectable: MAX_SHIPPINGS_PER_ROUTING,
              onSelectAll: handleSelectAll,
              onSelectRow: handleSelectShipping,
              getRowKey: (rowData) =>
                String((rowData as ShippingListApi).number),
            })}
            pagination={{
              totalItems,
              totalPages,
              currentPage,
              rowsPerPage: pageSize,
              onPageChange: onPageChange,
              onRowsPerPageChange: onPageSizeChange,
              rowsPerPageOptions: [50, 100, 150, 200],
            }}
          />
        </div>
      ) : (
        <div className='text-center py-8'>
          <p className='text-gray-600 text-lg'>No hay datos disponibles</p>
        </div>
      )}

      {assigningShipping && (
        <AssignShippingModal
          open={!!assigningShipping}
          onClose={() => setAssigningShipping(null)}
          shippingNumber={assigningShipping.number}
          logisticOperatorId={assigningShipping.logisticOperatorId ?? ''}
        />
      )}
    </div>
  );
};

export default ShippingsTable;
